// Context registry API — entities, attributes, relationships, metrics, needs
// and identity cross-references.
//
// All org-scoped: the registry is shared by every app in the org, which is what
// lets a second and third agent reuse the first one's entity definitions with
// no new data plumbing.
//
// There is deliberately no deploy step for these rows. context-service reads
// them live from the platform DB, so a save here takes effect on the next
// assemble — no builder→runtime copy to fall out of sync.

import api from './api';

const base = (orgId) => `/orgs/${orgId}/context`;

// ── entities ─────────────────────────────────────────────────────────────
// `{ slim: true }` asks for the map's projection — enough to draw a node and
// its wires, without the fields that make a row big (a need's SQL, an input
// spec, a description). The map loads every row in an org at once; the
// fetchContext*Detail calls below serve one row when a node is opened.
const slimParams = (opts) => (opts?.slim ? { slim: 1 } : undefined);

export const fetchContextEntities = async (orgId, opts) =>
  (await api.get(`${base(orgId)}/entities`, { params: slimParams(opts) })).data;

// One entity, with its attributes and outgoing relationships in the same
// response — a reader always wants all three, and three round trips to render
// one popup is three chances to show a half-drawn card.
export const fetchContextEntityDetail = async (orgId, uid) =>
  (await api.get(`${base(orgId)}/entities/${encodeURIComponent(uid)}`)).data;

export const createContextEntity = async (orgId, entity) =>
  (await api.post(`${base(orgId)}/entities`, entity)).data;

export const updateContextEntity = async (orgId, uid, entity) =>
  (await api.put(`${base(orgId)}/entities/${encodeURIComponent(uid)}`, entity)).data;

export const deleteContextEntity = async (orgId, uid) =>
  (await api.delete(`${base(orgId)}/entities/${encodeURIComponent(uid)}`)).data;

export const fetchContextAttributes = async (orgId, uid) =>
  (await api.get(`${base(orgId)}/entities/${encodeURIComponent(uid)}/attributes`)).data;

// Replaces the whole attribute set in one transaction — is_pii drives evidence
// redaction, so a partially applied save would be a disclosure bug.
export const saveContextAttributes = async (orgId, uid, attributes) =>
  (await api.put(`${base(orgId)}/entities/${encodeURIComponent(uid)}/attributes`, { attributes })).data;

// ── relationships ────────────────────────────────────────────────────────
export const fetchContextRelationships = async (orgId, params = {}) =>
  (await api.get(`${base(orgId)}/relationships`, { params })).data;

export const createContextRelationship = async (orgId, rel) =>
  (await api.post(`${base(orgId)}/relationships`, rel)).data;

export const deleteContextRelationship = async (orgId, uid) =>
  (await api.delete(`${base(orgId)}/relationships/${encodeURIComponent(uid)}`)).data;

// ── metrics ──────────────────────────────────────────────────────────────
// A metric uid is unique per org, so no entity hint is needed here.
export const fetchContextMetricDetail = async (orgId, uid) =>
  (await api.get(`${base(orgId)}/metrics/${encodeURIComponent(uid)}`)).data;

export const fetchContextMetrics = async (orgId, params = {}) =>
  (await api.get(`${base(orgId)}/metrics`, { params })).data;

export const createContextMetric = async (orgId, metric) =>
  (await api.post(`${base(orgId)}/metrics`, metric)).data;

export const updateContextMetric = async (orgId, uid, metric) =>
  (await api.put(`${base(orgId)}/metrics/${encodeURIComponent(uid)}`, metric)).data;

export const deleteContextMetric = async (orgId, uid) =>
  (await api.delete(`${base(orgId)}/metrics/${encodeURIComponent(uid)}`)).data;

// ── needs ────────────────────────────────────────────────────────────────
// A need uid is unique per (org, ENTITY) — the same uid can exist generically
// and again scoped to one entity — so pass the entity the map drew it under.
// Without it the generic row wins, which is the runtime's own fallback order.
export const fetchContextNeedDetail = async (orgId, uid, entityUid) =>
  (await api.get(`${base(orgId)}/needs/${encodeURIComponent(uid)}`, {
    params: entityUid ? { entity: entityUid } : undefined,
  })).data;

export const fetchContextNeeds = async (orgId, params = {}) =>
  (await api.get(`${base(orgId)}/needs`, { params })).data;

export const createContextNeed = async (orgId, need) =>
  (await api.post(`${base(orgId)}/needs`, need)).data;

export const updateContextNeed = async (orgId, uid, need) =>
  (await api.put(`${base(orgId)}/needs/${encodeURIComponent(uid)}`, need)).data;

export const deleteContextNeed = async (orgId, uid) =>
  (await api.delete(`${base(orgId)}/needs/${encodeURIComponent(uid)}`)).data;

// ── identity ─────────────────────────────────────────────────────────────
export const fetchContextXref = async (orgId, params = {}) =>
  (await api.get(`${base(orgId)}/xref`, { params })).data;

// The steward queue: active mappings below the confidence context-service
// trusts. These are the ones a human has to look at.
export const fetchContextXrefReview = async (orgId, params = {}) =>
  (await api.get(`${base(orgId)}/xref/review`, { params })).data;

// Routes through context-service's append-only write path. NOT an update:
// confirming closes the current mapping and inserts a new one, so a decision
// taken last month stays explainable against the mapping that was true then.
export const confirmContextXref = async (orgId, payload) =>
  (await api.post(`${base(orgId)}/xref/confirm`, payload)).data;

// Create OR re-point a mapping — one call for both, because the table treats
// them identically: whatever is active for (source_system, source_id) is
// closed, and a new row is appended. Payload is { rows: [...] }, with
// entity_type on EACH row.
export const loadContextXref = async (orgId, payload) =>
  (await api.post(`${base(orgId)}/xref/load`, payload)).data;

// The delete this table supports: stamps valid_to on the active row so it
// stops resolving, and keeps it as history. There is no hard delete — removing
// the row would make past decisions taken against it unexplainable.
export const closeContextXref = async (orgId, payload) =>
  (await api.post(`${base(orgId)}/xref/close`, payload)).data;

export const resolveContextIdentity = async (orgId, payload) =>
  (await api.post(`${base(orgId)}/resolve`, payload)).data;

// ── playground ───────────────────────────────────────────────────────────
// The same call an agent makes. org_id is set server-side from the URL, so a
// payload cannot assemble against another tenant's registry.
export const assembleContext = async (orgId, payload) =>
  (await api.post(`${base(orgId)}/assemble`, payload)).data;
